import { historyWords as words } from "@/components/history/history-copy";
import { useTabDraft } from "@/components/ui/box-draft";
import { readMappedRoster, readRosterSource, rosterExportTemplateCsv, rosterTemplateCsv, ROSTER_FIELDS, summariseRoster } from "@/lib/registrar-roster-source";
import { prefillColumns, type ColumnMap, type SourceTable } from "@/lib/source-columns";
import { importsText as text, importsWords } from "./imports-copy";
import { columnsText, columnsWords, ROSTER_DERIVATION, ROSTER_FIELD_NOTES } from "./source-columns-copy";
import { columnMapOf, columnsBlockers, columnsPart, fieldLines } from "./source-columns-part";
import { SourceFormat, type FormatLine, type FormatShape } from "./SourceFormat";
import { useImportTask, type ImportSource } from "./useImportTask";

const BOX = "history-import-roster";
const COLUMNS = `${BOX}-columns`;

const FORMAT: FormatLine[] = [
  { column: "kind", readsWhen: "Every row", note: "student or enrollment" },
  { column: "student_id", readsWhen: "student", note: "The learner's existing IMAM SID. Leave it blank to issue a new SID." },
  { column: "given_name, family_name", readsWhen: "student", note: "Required. With an SID they corroborate the record; they never find one." },
  { column: "delivery_email", readsWhen: "student", note: "The contact a later invitation would use. Never an identity." },
  { column: "program_key, cohort_key, admitted_year", readsWhen: "student", note: "Optional placement. admitted_year sets a new SID's cohort." },
  { column: "student_key", readsWhen: "enrollment", note: "The student's SID, or given name|family name|delivery email for a new student" },
  { column: "course_code, term_name", readsWhen: "enrollment", note: "An existing section in an open term" },
];

const SHAPES: FormatShape[] = [
  { title: columnsWords.templateShape, note: importsWords.templateNote, readsWhen: importsWords.readsWhen, lines: FORMAT,
    template: { name: "registrar-roster-template.csv", verb: importsWords.downloadTemplate, text: rosterTemplateCsv } },
  { title: columnsWords.exportShape, note: columnsWords.exportNote, readsWhen: columnsWords.needed, lines: fieldLines(ROSTER_FIELDS, ROSTER_FIELD_NOTES),
    template: { name: "registrar-roster-export-example.csv", verb: columnsWords.downloadExample, text: rosterExportTemplateCsv } },
];

/** An export's table, kept in this tab with the source it was read into, so its columns can be chosen again. */
interface KeptTable { digest: string; table: SourceTable }

export function useRegistrarRosterBox(rank: number | null = null) {
  const kept = useTabDraft<KeptTable | null>({ box: COLUMNS, record: "table", version: null, initial: null });
  const tableOf = (source: ImportSource | null) => source && kept.value?.digest === source.digest ? kept.value.table : null;
  const sourceParts = (source: ImportSource | null, setSource: (s: ImportSource | null) => void) => {
    const table = tableOf(source);
    const remap = (map: ColumnMap) => {
      if (!source || !table) return;
      const reading = readMappedRoster(table, map);
      setSource({ ...source, rows: reading.rows, problems: reading.problems, review: map });
    };
    return [columnsPart({ id: COLUMNS, source, table, fields: ROSTER_FIELDS, derivation: ROSTER_DERIVATION, onMap: remap })];
  };
  return useImportTask(BOX, words.registrarRoster, "registrar_roster", (source, setSource) => {
    const read = async (file: File | undefined) => {
      if (!file) return;
      try {
        const bytes = await file.arrayBuffer();
        const digest = await crypto.subtle.digest("SHA-256", bytes);
        const result = readRosterSource(file.name, new TextDecoder().decode(bytes));
        const hash = Array.from(new Uint8Array(digest), (v) => v.toString(16).padStart(2, "0")).join("");
        kept.set(result.table ? { digest: hash, table: result.table } : null);
        if (!result.table) { setSource({ name: file.name, digest: hash, scope: hash, rows: result.rows, problems: result.problems }); return; }
        const map = prefillColumns(ROSTER_FIELDS, result.table.header);
        const mapped = readMappedRoster(result.table, map);
        setSource({ name: file.name, digest: hash, scope: hash, rows: mapped.rows, problems: mapped.problems, review: map });
      } catch (e) {
        setSource({ name: file.name, digest: "", rows: [], problems: [e instanceof Error ? e.message : words.thisFileCouldNotBeRead] });
      }
    };
    const counts = source ? summariseRoster(source.rows) : null;
    const table = tableOf(source);
    return <div className="grid gap-3 text-ui">
      <p>{columnsWords.rosterIntro}</p>
      <SourceFormat shapes={SHAPES} />
      <label className="grid gap-2">{words.rosterFile}<input type="file" accept=".csv,.json" onChange={(e) => void read(e.target.files?.[0])} /></label>
      {source && counts && <p>{text.fileRows(source.name, source.rows.length)}{source.rows.length ? ` · ${text.rosterSummary(counts)}` : ""}</p>}
      {source && columnMapOf(source) && table && <p className="text-muted-foreground">{columnsText.exportLines(table.records.length)}</p>}
      {source?.problems.map((problem) => <p role="alert" key={problem}>{problem}</p>)}
    </div>;
  }, { emptyDetail: importsWords.chooseFileOrTemplate, sourceParts, sourceBlockers: (source) => columnsBlockers(COLUMNS, source, ROSTER_FIELDS) }, rank).box;
}
